import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import {
  TFunction,
  getUserFacingApiErrorMessage,
  useAddCropMixComposerCrops,
  useAddFieldCropPractice,
  useCalculateCoverCropMixes,
  useCancelAsyncOnUnmount,
  useDeleteCropMixComposerSettings,
  useGeneralContext,
  useGetCropMixComposerCrops,
  useGetCropMixComposerScoreFactors,
  useGetCropMixComposerSettings,
  useGetFieldPractices,
  useGetPracticeCrops,
  useLocation,
  useMutateCropMixComposerSettings,
  useNavigate,
  useParams,
  useTranslation,
  useUserContext,
  useValidateCropPeriods,
} from "utils";

import { CALCULATE_COVER_CROP_MIXES, COVER_CROP_MIX_TYPE, GET_FIELDS_CROP_RECOMMENDATIONS } from "consts";

import {
  CMC_MIX_TYPE,
  CROP_MIX_COMPOSER_GOALS,
  CROP_ROTATION,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropItemData,
  CropMixComposerCrop,
  CropMixComposerGoalResponse,
  CropMixComposerSettings,
  MainCropSettingsCMC,
  MixDistributionShare,
  MutateCropMixComposerPreferencesRequest,
  TimelineCalendarItemsProps,
} from "models";

import { Drawer, ExtraInfo, Flex, MainPage, Message, PageSpin, Trans } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { AutoMixLoadingScreen } from "pages/app/crop-mix-composer/components/AutoMixLoadingScreen";
import { CropMixComposerFooter } from "pages/app/crop-mix-composer/components/CropMixComposerFooter";
import { CropMixComposerHeader } from "pages/app/crop-mix-composer/components/CropMixComposerHeader.tsx";
import { CropMixComposerInfoSection } from "pages/app/crop-mix-composer/components/CropMixComposerInfoSection.tsx";
import { GoalsSelection } from "pages/app/crop-mix-composer/components/GoalsSelection";
import { MixComposition } from "pages/app/crop-mix-composer/components/MixComposition";
import { MixOverview } from "pages/app/crop-mix-composer/components/MixOverview";
import { CROP_FAMILIES_TO_EXCLUDE_OPTIONS, SequenceAndConditions } from "pages/app/crop-mix-composer/components/SequenceAndConditions";
import { CommercialCropMixesContent } from "pages/app/crop-mix-composer/components/commercialMix/CommercialCropMixes.tsx";
import {
  addMainCropToRotation,
  buildAutoGeneratedMix,
  calculateMixDistributionShares,
  getCropsBeforeAndAfter,
  getMostRecentlyUpdatedMix,
  getNextAvailableMixName,
  getNextCrop,
  isMixResponseOutdated,
  validateOnboarding1,
} from "pages/app/crop-mix-composer/utils/cropMixComposer";
import { useActiveCropMixSettings } from "pages/app/crop-mix-composer/utils/useActiveCropMixSettings";
import { useCreateDraftMix } from "pages/app/crop-mix-composer/utils/useCreateDraftMix";

export enum CMC_ROUTES {
  COMPOSITION = "composition",
  GOALS = "goals",
  OVERVIEW = "overview",
  SETTINGS = "settings",
  COM_MIXES = "com-mixes",
}

const ONBOARDING_STEPS_ORDER = [CMC_ROUTES.SETTINGS, CMC_ROUTES.GOALS, CMC_ROUTES.COMPOSITION, CMC_ROUTES.OVERVIEW];

export const MAX_SELECTABLE_GOALS = 3;

export enum CMC_ONBOARDING_1_ERRORS {
  SOIL_TYPE = "soilType",
  SOIL_PH = "soilPh",
  DROUGHT = "drought",
  MAIN_CROP_1 = "mainCrop1",
  MAIN_CROP_2 = "mainCrop2",
  SEEDING_BEFORE_HARVEST = "seedingBeforeHarvest",
  MINIMUM_GROWING_PERIOD = "minimumGrowingPeriod",
  LEGUME_EXCLUSION_CONFIRMATION = "legumeExclusionConfirmation",
}

const Settings = ({
  setShowInfoDrawer,
  t,
  fieldId,
  selectedRecommendationItemOnTimeline,
}: {
  setShowInfoDrawer: (show: boolean) => void;
  t: TFunction;
  fieldId?: string;
  selectedRecommendationItemOnTimeline?: TimelineCalendarItemsProps<CropItemData>;
}) => (
  <>
    <CropMixComposerInfoSection
      className="info"
      headline={t(`page.${CMC_ROUTES.SETTINGS}.info.headline`)}
      content={t(`page.${CMC_ROUTES.SETTINGS}.info.content`)}
      info={t(`page.${CMC_ROUTES.SETTINGS}.info.info`)}
      onInfoClicked={setShowInfoDrawer}
      dataTestId="sequence-and-conditions"
    />
    <div className="content" data-testid={"crop-mix-composer-overlay-step-sequence-and-conditions"}>
      <SequenceAndConditions fieldId={fieldId} selectedRecommendationItemOnTimeline={selectedRecommendationItemOnTimeline} />
    </div>
  </>
);

const Goals = ({ setShowInfoDrawer, maxSelectableItems, t }: { setShowInfoDrawer: (show: boolean) => void; maxSelectableItems: number; t: TFunction }) => {
  const { autoMixMode, isChangingStep } = useCropMixComposerContext();
  const activeSettingsCMC = useActiveCropMixSettings();

  const mixType = activeSettingsCMC?.mixType || null;

  return autoMixMode && isChangingStep !== null && !mixType ? (
    <AutoMixLoadingScreen />
  ) : (
    <>
      <CropMixComposerInfoSection
        className="info"
        headline={t(`page.${CMC_ROUTES.GOALS}.info.headline`)}
        content={<Trans i18nKey={`cropMixComposer:page.${CMC_ROUTES.GOALS}.info.content`} components={{ strong: <strong /> }} />}
        info={t(`page.${CMC_ROUTES.GOALS}.info.info`)}
        onInfoClicked={setShowInfoDrawer}
        dataTestId="goals"
      />
      <div className="content">
        <GoalsSelection items={CROP_MIX_COMPOSER_GOALS} maxSelectableItems={maxSelectableItems} t={t} />
      </div>
    </>
  );
};

export const CropMixComposerOverlay = ({
  visible,
  regionId,
  setShowCropMixComposer,
  openInDrawer,
  fieldId,
}: {
  visible: boolean;
  regionId?: string;
  setShowCropMixComposer?: Dispatch<SetStateAction<boolean>>;
  openInDrawer?: boolean;
  fieldId?: string;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const signal = useCancelAsyncOnUnmount()();
  const queryClient = useQueryClient();

  const {
    activeCropMixId,
    autoMixMode,
    cropRotationFamiliesToExclude,
    currentStep,
    drought,
    isDraftMix,
    isPageLoading,
    legumeExclusionConfirmation,
    mainCropSettings,
    selectedCrops,
    selectedGoals,
    selectedRecommendationItemOnTimeline,
    soilPh,
    soilType,
    weightShareDistribution,
    setActiveCropMixId,
    setCMCMixCropFCPO,
    setCropRotationFamiliesToExclude,
    setCurrentStep,
    setDrought,
    setIsChangingStep,
    setIsDraftMix,
    setIsNewCropMix,
    setIsPageLoading,
    setLegumeExclusionConfirmation,
    setMainCropSettings,
    setOnboarding1Errors,
    setRegionId,
    setSelectedCrops,
    setSelectedGoals,
    setShowDrawerFromCMC,
    setSoilPh,
    setSoilType,
    setWeightShareDistribution,
    setShowAutoMixFailureMessage,
    hasSettingsChanged,
    setHasSettingsChanged,
    resetSettings,
  } = useCropMixComposerContext();
  const { setNavigationCrumbs } = useGeneralContext();
  const { user } = useUserContext();

  const [showInfoDrawer, setShowInfoDrawer] = useState<boolean>(false);
  const [isRegeneratingSmartMix, setIsRegeneratingSmartMix] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const isCmcPath = location.pathname.startsWith("/cmc");
  const isVisible = visible || isCmcPath;
  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;

  const { data: cropMixComposerResponse, isPending: isPendingCropMixComposerResponse } = useCalculateCoverCropMixes({ cropMixId: params.cropMixId });
  const { data: settingsCMC, isPending: isPendingSettingsCMC } = useGetCropMixComposerSettings();

  const { mutateAsync: mutateAsyncCropMixComposerSettings } = useMutateCropMixComposerSettings();
  const { data: cropsCMC, isPending: isPendingCropsCMC } = useGetCropMixComposerCrops(params.cropMixId);
  const { mutateAsync: mutateAsyncAddCropMixComposerCrops } = useAddCropMixComposerCrops();
  const { isPending: isPendingScoreFactors } = useGetCropMixComposerScoreFactors();
  const { data: crops } = useGetPracticeCrops({ regionId, enabled: true });
  const { data: practicesResponse, isLoading: isLoadingPracticesResponse } = useGetFieldPractices({ fieldId: fieldId || "", inView: !!fieldId });
  const { mutateAsync: addFieldCropPractice } = useAddFieldCropPractice({ fieldId: params?.fieldId || "", farmId: user?.farm?.id || undefined });
  const { mutateAsync: deleteCropMixComposerSettings } = useDeleteCropMixComposerSettings();
  const { validatePeriods } = useValidateCropPeriods();
  const { createDraftMix } = useCreateDraftMix();

  const isPageEntry = !fieldId && !openInDrawer;
  const areSettingsSettled = !isPendingSettingsCMC;
  const hasNoSavedMix = (settingsCMC?.length ?? 0) === 0;
  const isPageDraftEntry = isVisible && isCmcPath && isPageEntry && areSettingsSettled && (hasNoSavedMix || isDraftMix);
  const isFieldViewDraftEntry = !!fieldId && visible && !!practicesResponse && !isLoadingPracticesResponse;
  const isDraft = !params.cropMixId && (isPageDraftEntry || isFieldViewDraftEntry);

  const isValidStepFromPath = useCallback(() => {
    const isValidStep = !!params.step && Object.values(CMC_ROUTES).includes(params.step as CMC_ROUTES);
    return isValidStep;
  }, [params.step]);

  const updateLocalSettings = useCallback(
    (cmcSettings: CropMixComposerSettings, cropsCMC: CropMixComposerCrop[], cropMixId: string) => {
      if (!cmcSettings) return;

      const { goals, recommendation } = cmcSettings;
      const { settings, mainCropSettings } = recommendation;

      setSelectedGoals(goals);
      setSoilType(settings.soilType || undefined);
      setSoilPh(settings.soilPh || undefined);
      setDrought(typeof settings.isDrought === "boolean" ? settings.isDrought : undefined);

      const savedCrop1Family = mainCropSettings.crop1Id ? crops?.find((crop) => crop.id === mainCropSettings.crop1Id)?.cropFamily : null;
      const savedCrop2Family = mainCropSettings.crop2Id ? crops?.find((crop) => crop.id === mainCropSettings.crop2Id)?.cropFamily : null;

      // if we one or more main crops are missing, step 1 isn't saved, so the legume confirmation should be "null"
      // if we have legumes saved in the DB, the confirmation selections should be "false"
      // if one of the main crops is a legume, but legume isn't saved in the DB, the confirmation should be "true"
      // in any other case, we keep the confirmation as unset -> "null"
      const updatedLegumeExclusionConfirmation =
        !mainCropSettings.crop1Id || !mainCropSettings.crop2Id
          ? null
          : settings.cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE)
          ? false
          : savedCrop1Family === CROP_ROTATION.FABACEAE || savedCrop2Family === CROP_ROTATION.FABACEAE
          ? true
          : null;

      // for the interface to make sense, we need to inject the legume family in the excluded list if the "updatedLegumeExclusionConfirmation" is "true",
      // otherwise this will break the logic if a user changes the legume exclusion confirmation preference without changing any other value
      const updatedCropRotationFamiliesToExclude =
        updatedLegumeExclusionConfirmation === true
          ? [...settings.cropRotationFamiliesToExclude, CROP_ROTATION.FABACEAE]
          : settings.cropRotationFamiliesToExclude;

      setLegumeExclusionConfirmation(updatedLegumeExclusionConfirmation);
      setCropRotationFamiliesToExclude(updatedCropRotationFamiliesToExclude);

      const updatedMainCropSettings: MainCropSettingsCMC = {
        crop1Id: mainCropSettings.crop1Id || undefined,
        crop2Id: mainCropSettings.crop2Id || undefined,
        seedingDate: mainCropSettings.seedingDate || undefined,
        harvestDate: mainCropSettings.harvestDate || undefined,
      };

      setMainCropSettings(updatedMainCropSettings);

      let updatedSelectedCrops: CropMixComposerGoalResponse[] = [];
      let updatedWeightShareDistribution: MixDistributionShare = {};

      if (cropMixComposerResponse) {
        updatedSelectedCrops =
          !cropsCMC || cropsCMC.length === 0
            ? []
            : cropsCMC
                .map((crop) => cropMixComposerResponse.find((c) => c.crop.id === crop.cropId))
                .filter((crop): crop is CropMixComposerGoalResponse => crop !== undefined);
        updatedWeightShareDistribution = !cropsCMC || cropsCMC.length === 0 ? {} : calculateMixDistributionShares(updatedSelectedCrops, cropsCMC);

        setSelectedCrops(updatedSelectedCrops);
        setWeightShareDistribution(updatedWeightShareDistribution);
      }

      const { isValid: settingsCompleted } = validateOnboarding1(
        {
          crop1Id: mainCropSettings.crop1Id || undefined,
          crop2Id: mainCropSettings.crop2Id || undefined,
          seedingDate: mainCropSettings.seedingDate || undefined,
          harvestDate: mainCropSettings.harvestDate || undefined,
        },
        settings.soilType,
        settings.soilPh,
        settings.isDrought,
        settings.cropRotationFamiliesToExclude,
        legumeExclusionConfirmation
      );
      const goalsCompleted = settingsCompleted && goals.length === MAX_SELECTABLE_GOALS;
      const compositionCompleted =
        settingsCompleted &&
        goalsCompleted &&
        updatedSelectedCrops.length > 0 &&
        Object.keys(updatedWeightShareDistribution).length === updatedSelectedCrops.length;

      const updatedStep =
        currentStep === CMC_ROUTES.COM_MIXES && compositionCompleted
          ? CMC_ROUTES.COM_MIXES
          : compositionCompleted
          ? CMC_ROUTES.OVERVIEW
          : goalsCompleted
          ? CMC_ROUTES.COMPOSITION
          : settingsCompleted
          ? CMC_ROUTES.GOALS
          : CMC_ROUTES.SETTINGS;
      setActiveCropMixId(cropMixId);

      const mixResponseUpdatedAt = queryClient.getQueryState([CALCULATE_COVER_CROP_MIXES, cropMixId])?.dataUpdatedAt;
      if (settingsCompleted && goalsCompleted && isMixResponseOutdated(mixResponseUpdatedAt, cmcSettings.lastUpdated)) {
        queryClient.invalidateQueries({ queryKey: [CALCULATE_COVER_CROP_MIXES, cropMixId] });
      }

      const isExistingStep = isValidStepFromPath();
      const isValidStep =
        currentStep === updatedStep ||
        (currentStep === CMC_ROUTES.COM_MIXES && compositionCompleted) ||
        (currentStep === CMC_ROUTES.OVERVIEW && compositionCompleted) ||
        (currentStep === CMC_ROUTES.COMPOSITION && goalsCompleted) ||
        (currentStep === CMC_ROUTES.GOALS && settingsCompleted) ||
        currentStep === CMC_ROUTES.SETTINGS;

      if (!isExistingStep || !isValidStep) {
        setCurrentStep(updatedStep);
      }
    },
    [
      cropMixComposerResponse,
      crops,
      currentStep,
      isValidStepFromPath,
      legumeExclusionConfirmation,
      queryClient,
      setActiveCropMixId,
      setCropRotationFamiliesToExclude,
      setCurrentStep,
      setDrought,
      setLegumeExclusionConfirmation,
      setMainCropSettings,
      setSelectedCrops,
      setSelectedGoals,
      setSoilPh,
      setSoilType,
      setWeightShareDistribution,
    ]
  );

  const hasLocalSettingsChanged = useCallback(
    (savedPreferences: CropMixComposerSettings): boolean => {
      if (!savedPreferences?.recommendation?.settings || !savedPreferences?.recommendation?.mainCropSettings) return false;

      const hasSoilSettingsChanged =
        soilType !== savedPreferences.recommendation.settings.soilType ||
        soilPh !== savedPreferences.recommendation.settings.soilPh ||
        drought !== savedPreferences.recommendation.settings.isDrought;

      const updatedCropRotationFamiliesToExclude =
        cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === true
          ? cropRotationFamiliesToExclude.filter((family) => family !== CROP_ROTATION.FABACEAE)
          : cropRotationFamiliesToExclude;

      const hasCropRotationFamiliesToExcludeChanged =
        updatedCropRotationFamiliesToExclude.length !== savedPreferences.recommendation.settings.cropRotationFamiliesToExclude.length ||
        updatedCropRotationFamiliesToExclude.some((family) => !savedPreferences.recommendation.settings.cropRotationFamiliesToExclude.includes(family));

      const hasMainCropSettingsChanged =
        mainCropSettings?.crop1Id !== savedPreferences.recommendation.mainCropSettings.crop1Id ||
        mainCropSettings?.crop2Id !== savedPreferences.recommendation.mainCropSettings.crop2Id ||
        mainCropSettings?.seedingDate !== savedPreferences.recommendation.mainCropSettings.seedingDate ||
        mainCropSettings?.harvestDate !== savedPreferences.recommendation.mainCropSettings.harvestDate;

      const hasGoalsChanged = selectedGoals.length !== savedPreferences.goals.length || !selectedGoals.every((goal) => savedPreferences.goals.includes(goal));

      return hasSoilSettingsChanged || hasCropRotationFamiliesToExcludeChanged || hasMainCropSettingsChanged || hasGoalsChanged;
    },
    [cropRotationFamiliesToExclude, drought, legumeExclusionConfirmation, mainCropSettings, selectedGoals, soilPh, soilType]
  );

  const updatePreferences = useCallback(async () => {
    if (!activeCropMixId) return;

    const savedPreferences = settingsCMC?.find((mix) => mix.id === activeCropMixId);
    if (!savedPreferences) return;

    if (!hasLocalSettingsChanged(savedPreferences)) return;

    const requestBody: MutateCropMixComposerPreferencesRequest = {
      name: savedPreferences.name,
      goals: selectedGoals,
      recommendation: {
        settings: {
          soilType,
          soilPh,
          isDrought: drought,
          cropRotationFamiliesToExclude:
            cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === true
              ? cropRotationFamiliesToExclude.filter((family) => family !== CROP_ROTATION.FABACEAE)
              : cropRotationFamiliesToExclude,
        },
        mainCropSettings,
      },
    };

    await mutateAsyncCropMixComposerSettings({ data: requestBody, settingId: activeCropMixId });
  }, [
    activeCropMixId,
    settingsCMC,
    hasLocalSettingsChanged,
    selectedGoals,
    soilType,
    soilPh,
    drought,
    cropRotationFamiliesToExclude,
    legumeExclusionConfirmation,
    mainCropSettings,
    mutateAsyncCropMixComposerSettings,
  ]);

  const hasLocalCropsCompositionChanged = useCallback((newMixDistributionShare: MixDistributionShare, savedComposition: CropMixComposerCrop[] | undefined) => {
    const crops = Object.entries(newMixDistributionShare);
    const newComposition = crops.map(([cropId, mixDistributionShare]) => ({
      cropId,
      weightShare: mixDistributionShare.weightShare,
    }));

    if (newComposition.length === 0 && (!savedComposition || savedComposition.length === 0)) {
      return false;
    }

    if (newComposition.length !== (savedComposition?.length || 0)) {
      return true;
    }

    return newComposition.some((crop) => {
      const savedCrop = savedComposition?.find((c) => c.cropId === crop.cropId);
      if (!savedCrop) return true;

      return savedCrop.weightShare !== crop.weightShare;
    });
  }, []);

  const updateSelectedCrops = useCallback(
    async (activeCropMixId: string | undefined, weightShareDistribution: MixDistributionShare, cropsCMC: CropMixComposerCrop[] | undefined) => {
      if (!activeCropMixId) return;

      // Check if the composition has changed
      const hasCompositionChanged = hasLocalCropsCompositionChanged(weightShareDistribution, cropsCMC);

      if (!hasCompositionChanged) return;

      const crops = Object.entries(weightShareDistribution);
      if (!crops || crops.length === 0) return;

      const composition = crops.map(([cropId, mixDistributionShare]) => ({
        cropId,
        weightShare: mixDistributionShare.weightShare,
      }));

      await mutateAsyncAddCropMixComposerCrops({ cropMixId: activeCropMixId, data: { composition } });
    },
    [hasLocalCropsCompositionChanged, mutateAsyncAddCropMixComposerCrops]
  );

  const handleClose = () => {
    if (params?.cropMixId && (!selectedRecommendationItemOnTimeline?.data.cropMixId || !settingsCMC?.some((mix) => mix.id === params.cropMixId))) {
      deleteCropMixComposerSettings({ cropMixId: params?.cropMixId ?? "" });
    }

    setShowCropMixComposer?.(false);
    resetSettings();
    navigate(basePath);
  };

  const findNextStep = (currentStep: CMC_ROUTES | null, direction: "next" | "previous") => {
    if (!currentStep) return CMC_ROUTES.SETTINGS;

    const currentStepIndex = ONBOARDING_STEPS_ORDER.indexOf(currentStep);
    const nextStepIndex = direction === "next" ? currentStepIndex + 1 : currentStepIndex - 1;

    return ONBOARDING_STEPS_ORDER[nextStepIndex] || currentStep;
  };

  const getUpdatedMixType = (currentMixType: CMC_MIX_TYPE | null, hasCompositionChanged: boolean) => {
    if (currentMixType && !hasCompositionChanged) return currentMixType;
    if (!currentMixType) return CMC_MIX_TYPE.MANUAL;
    if (currentMixType === CMC_MIX_TYPE.AUTO_GENERATED && hasCompositionChanged) return CMC_MIX_TYPE.AUTO_GENERATED_EDITED;
    return currentMixType;
  };

  const handleRegenerateSmartMix = async () => {
    const activeCropMixSettings = settingsCMC?.find((mix) => mix.id === activeCropMixId);
    if (!activeCropMixId || !activeCropMixSettings || isRegeneratingSmartMix) return;

    setIsRegeneratingSmartMix(true);

    try {
      await queryClient.refetchQueries({ queryKey: [CALCULATE_COVER_CROP_MIXES, activeCropMixId] });
      const cropMixComposerResponse: CropMixComposerGoalResponse[] = queryClient.getQueryData([CALCULATE_COVER_CROP_MIXES, activeCropMixId]) || [];

      const { crops: autoSelectedCrops, weightShareDistribution: autoWeightShareDistribution, isEmpty } = buildAutoGeneratedMix(cropMixComposerResponse);

      if (isEmpty) {
        setShowAutoMixFailureMessage(true);
        navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.COMPOSITION}${location.search}`);
        return;
      }

      setSelectedCrops(autoSelectedCrops);
      setWeightShareDistribution(autoWeightShareDistribution);
      await updateSelectedCrops(activeCropMixId, autoWeightShareDistribution, cropsCMC);

      await mutateAsyncCropMixComposerSettings({
        data: { mixType: CMC_MIX_TYPE.AUTO_GENERATED, name: activeCropMixSettings.name },
        settingId: activeCropMixId,
      });
    } catch (regenerateError) {
      Message({ content: getUserFacingApiErrorMessage(regenerateError, t("errors.saveStepFailed")), icon: "error" });
    } finally {
      setIsRegeneratingSmartMix(false);
    }
  };

  const handleNextButton = async (mixType: CMC_MIX_TYPE | null) => {
    setIsChangingStep("next");

    const { isValid: settingsCompleted, errors: onboarding1ValidationErrors } = validateOnboarding1(
      mainCropSettings,
      soilType,
      soilPh,
      drought,
      cropRotationFamiliesToExclude,
      legumeExclusionConfirmation
    );
    const goalsCompleted = selectedGoals.length === MAX_SELECTABLE_GOALS;
    const compositionCompleted = selectedCrops.length > 0 && Object.keys(weightShareDistribution).length === selectedCrops.length;

    setOnboarding1Errors(onboarding1ValidationErrors);

    try {
      if (isDraft) {
        if (!settingsCompleted) {
          Message({ content: t(`errors.${CMC_ROUTES.SETTINGS}`), icon: "error" });
          return;
        }

        const newMix = await createDraftMix();
        if (newMix) navigate(`${basePath}cmc/${newMix.id}/${CMC_ROUTES.GOALS}${location.search}`);
        return;
      }

      if (currentStep === CMC_ROUTES.SETTINGS && !settingsCompleted) {
        Message({ content: t(`errors.${CMC_ROUTES.SETTINGS}`), icon: "error" });
        return;
      }

      if (currentStep === CMC_ROUTES.GOALS && !goalsCompleted) {
        Message({ content: t(`errors.${CMC_ROUTES.GOALS}`, { goalCount: MAX_SELECTABLE_GOALS }), icon: "error" });
        return;
      }

      if (currentStep === CMC_ROUTES.COMPOSITION && !compositionCompleted) {
        Message({ content: t(`errors.${CMC_ROUTES.COMPOSITION}`), icon: "error" });
        return;
      }

      const activeCropMixSettings = settingsCMC?.find((mix) => mix.id === activeCropMixId) as CropMixComposerSettings;
      const shouldUpdateSettings = hasLocalSettingsChanged(activeCropMixSettings);
      const hasCompositionChanged = hasLocalCropsCompositionChanged(weightShareDistribution, cropsCMC);

      // since we are saving the settings at every step, we want to invalidate the query only once if either step 1 or 2 have changes
      if (!hasSettingsChanged) setHasSettingsChanged(shouldUpdateSettings);

      const shouldInvalidateCropMixComposerResponseData = shouldUpdateSettings || hasSettingsChanged;

      await Promise.all([updatePreferences(), updateSelectedCrops(activeCropMixId, weightShareDistribution, cropsCMC)]);

      // if we are in auto mix mode, we want to fetch the data from the microservice and update the selected crops
      // if we have the mix type, it means we are in edit mode, so we don't want to change the mix composition, just recalculate the match score and the goals
      if (currentStep === CMC_ROUTES.GOALS && autoMixMode && !mixType) {
        // Add a minimum wait time to show the loading screen
        const updatedCropMixComposerResponse: CropMixComposerGoalResponse[] = await Promise.all([
          queryClient.fetchQuery({
            queryKey: [CALCULATE_COVER_CROP_MIXES, activeCropMixId],
          }) as Promise<CropMixComposerGoalResponse[]>,
          new Promise<void>((resolve) => {
            setTimeout(resolve, 4500);
          }),
        ]).then(([response]) => response);

        const {
          crops: updatedSelectedCrops,
          weightShareDistribution: updatedWeightShareDistribution,
          isEmpty,
        } = buildAutoGeneratedMix(updatedCropMixComposerResponse);

        setSelectedCrops(updatedSelectedCrops);
        setWeightShareDistribution(updatedWeightShareDistribution);
        await updateSelectedCrops(activeCropMixId, updatedWeightShareDistribution, cropsCMC);

        setHasSettingsChanged(false);
        setShowAutoMixFailureMessage(isEmpty);

        if (updatedSelectedCrops.length > 0 && Object.keys(updatedWeightShareDistribution).length === updatedSelectedCrops.length) {
          await mutateAsyncCropMixComposerSettings({
            data: { mixType: CMC_MIX_TYPE.AUTO_GENERATED, name: activeCropMixSettings.name },
            settingId: activeCropMixId,
          });
          navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.OVERVIEW}${location.search}`);
          return;
        }
      }

      // if we are on the goals page and we don't have the mix type, it means we are in the onboarding flow, in this case we must fetch the crops data from the microservice
      // if we are in any step and we have a mix type, it means we are in edit mode, so we should fetch the crops data from the microservice if the settings have changed and recalculate the mix
      if ((currentStep === CMC_ROUTES.GOALS && !mixType) || (mixType && shouldInvalidateCropMixComposerResponseData)) {
        await queryClient.refetchQueries({
          queryKey: [CALCULATE_COVER_CROP_MIXES, activeCropMixId],
        });
        const updatedCropMixComposerResponse: CropMixComposerGoalResponse[] = queryClient.getQueryData([CALCULATE_COVER_CROP_MIXES, activeCropMixId]) || [];

        // When auto-mix fetchQuery returned stale empty data but refetch has real results,
        // retry auto-selection from the fresh response
        if (autoMixMode && !mixType && selectedCrops.length === 0 && updatedCropMixComposerResponse.length > 0) {
          const {
            crops: autoSelectedCrops,
            weightShareDistribution: autoWeightShareDistribution,
            isEmpty,
          } = buildAutoGeneratedMix(updatedCropMixComposerResponse);

          setSelectedCrops(autoSelectedCrops);
          setWeightShareDistribution(autoWeightShareDistribution);
          await updateSelectedCrops(activeCropMixId, autoWeightShareDistribution, cropsCMC);
          setHasSettingsChanged(false);

          if (isEmpty) {
            setShowAutoMixFailureMessage(true);
          }

          if (autoSelectedCrops.length > 0 && Object.keys(autoWeightShareDistribution).length === autoSelectedCrops.length) {
            mutateAsyncCropMixComposerSettings({
              data: { mixType: CMC_MIX_TYPE.AUTO_GENERATED, name: activeCropMixSettings.name },
              settingId: activeCropMixId,
            });
            navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.OVERVIEW}${location.search}`);
            return;
          }
        } else {
          const updatedSelectedCrops = selectedCrops
            .map((crop) => updatedCropMixComposerResponse.find((c) => c.crop.id === crop.crop.id))
            .filter((crop): crop is CropMixComposerGoalResponse => crop !== undefined);
          const updatedWeightShareDistribution = calculateMixDistributionShares(updatedSelectedCrops, cropsCMC);

          setSelectedCrops(updatedSelectedCrops);
          setWeightShareDistribution(updatedWeightShareDistribution);
          await updateSelectedCrops(activeCropMixId, updatedWeightShareDistribution, cropsCMC);
          setHasSettingsChanged(false);
        }
      }

      if (currentStep === CMC_ROUTES.COMPOSITION) {
        const newMixType = getUpdatedMixType(mixType, hasCompositionChanged);
        await mutateAsyncCropMixComposerSettings({ data: { mixType: newMixType, name: activeCropMixSettings.name }, settingId: activeCropMixId });
      }

      const nextStep = mixType ? CMC_ROUTES.OVERVIEW : findNextStep(currentStep, "next");
      navigate(`${basePath}cmc/${activeCropMixId}/${nextStep}${location.search}`);
    } catch (stepError) {
      Message({ content: getUserFacingApiErrorMessage(stepError, t(isDraft ? "errors.createMixFailed" : "errors.saveStepFailed")), icon: "error" });
    } finally {
      setIsChangingStep(null);
    }
  };

  const handleBackButton = async () => {
    setIsChangingStep("previous");

    try {
      const activeCropMixSettings = settingsCMC?.find((mix) => mix.id === activeCropMixId) as CropMixComposerSettings;
      const shouldUpdateSettings = hasLocalSettingsChanged(activeCropMixSettings);

      // since we are saving the settings at every step, we need to keep track of the changes in a separate state
      if (!hasSettingsChanged) setHasSettingsChanged(shouldUpdateSettings);

      await Promise.all([updatePreferences(), updateSelectedCrops(activeCropMixId, weightShareDistribution, cropsCMC)]);

      const previousStep = findNextStep(currentStep, "previous");
      navigate(`${basePath}cmc/${activeCropMixId}/${previousStep}${location.search}`);
    } finally {
      setIsChangingStep(null);
    }
  };

  const handleUpdateSettings = useCallback(async () => {
    const mixToUpdateName = getNextAvailableMixName(t("cropMixSelectionModal.myMix"), settingsCMC);
    const settingsRequestBody: MutateCropMixComposerPreferencesRequest = {
      name: mixToUpdateName,
      goals: selectedGoals,
      recommendation: {
        settings: {
          soilType,
          soilPh,
          isDrought: drought,
          cropRotationFamiliesToExclude:
            cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === true
              ? cropRotationFamiliesToExclude.filter((family) => family !== CROP_ROTATION.FABACEAE)
              : cropRotationFamiliesToExclude,
        },
        mainCropSettings,
      },
    };

    await mutateAsyncCropMixComposerSettings({ data: settingsRequestBody, settingId: activeCropMixId });
  }, [
    t,
    settingsCMC,
    mainCropSettings,
    mutateAsyncCropMixComposerSettings,
    selectedGoals,
    soilPh,
    soilType,
    drought,
    cropRotationFamiliesToExclude,
    legumeExclusionConfirmation,
    activeCropMixId,
  ]);

  const getMixTypeId = useCallback(
    (seedingYear: number, harvestYear: number, harvestMonth: number) => {
      const isSummerCrop = harvestYear === seedingYear && harvestMonth >= 5 && harvestMonth <= 10;
      const isMultiSpecies = selectedCrops.length > 1;

      if (isSummerCrop) {
        return isMultiSpecies ? COVER_CROP_MIX_TYPE.SUMMER_MULTI_SPECIES : COVER_CROP_MIX_TYPE.SUMMER_SINGLE_SPECIES;
      }

      return isMultiSpecies ? COVER_CROP_MIX_TYPE.WINTER_MULTI_SPECIES : COVER_CROP_MIX_TYPE.WINTER_SINGLE_SPECIES;
    },
    [selectedCrops]
  );

  const handleApplyToField = useCallback(async () => {
    const mainCropsBeforeAndAfterRecommendation = getCropsBeforeAndAfter(practicesResponse, selectedRecommendationItemOnTimeline);
    const nextFutureCrop = getNextCrop(practicesResponse, selectedRecommendationItemOnTimeline);

    await handleUpdateSettings();

    if (!mainCropsBeforeAndAfterRecommendation.after?.cropId) {
      await addMainCropToRotation(
        t,
        mainCropsBeforeAndAfterRecommendation,
        nextFutureCrop,
        mainCropSettings,
        crops || [],
        practicesResponse,
        fieldId || "",
        signal,
        validatePeriods,
        addFieldCropPractice
      );
    }

    queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });

    const seedingYear = moment(mainCropSettings?.harvestDate).year();
    const harvestYear = moment(mainCropSettings?.seedingDate).year();
    const harvestMonth = moment(mainCropSettings?.seedingDate).month() + 1;

    const cmcSeedingStart = mainCropSettings?.harvestDate ? moment(mainCropSettings.harvestDate).add(1, "day").toISOString() : null;
    const cmcHarvestEnd = mainCropSettings?.seedingDate ? moment(mainCropSettings.seedingDate).subtract(1, "day").toISOString() : null;

    const cmcMixCropFCPO: CropFCPO = {
      cropId: getMixTypeId(seedingYear, harvestYear, harvestMonth),
      cropType: CROP_TYPE_FCPO.COVER,
      seedingStartDate: cmcSeedingStart,
      seedingEndDate: null,
      harvestStartDate: null,
      harvestEndDate: cmcHarvestEnd,
      seedingPracticeType: null,
      hasResidue: null,
      cropMixId: activeCropMixId,
      id: selectedRecommendationItemOnTimeline?.id?.toString() || "",
      cropName: selectedRecommendationItemOnTimeline?.data.cropName || "",
      isConfirmed: false,
      translationSlug: "",
      yieldPerHa: null,
      yieldPerHaUnit: null,
      year: seedingYear,
      seedingVerificationStatus: null,
      fallbackDates: {
        seedingStartDate: cmcSeedingStart,
        harvestEndDate: cmcHarvestEnd,
      },
    };

    setCMCMixCropFCPO(cmcMixCropFCPO);
    setShowDrawerFromCMC(true);
  }, [
    crops,
    signal,
    validatePeriods,
    addFieldCropPractice,
    t,
    activeCropMixId,
    fieldId,
    getMixTypeId,
    handleUpdateSettings,
    mainCropSettings,
    practicesResponse,
    queryClient,
    selectedRecommendationItemOnTimeline,
    setCMCMixCropFCPO,
    setShowDrawerFromCMC,
  ]);

  const seedDraftFromFieldView = useCallback(() => {
    const mainCropsBeforeAndAfterRecommendation = getCropsBeforeAndAfter(practicesResponse, selectedRecommendationItemOnTimeline);

    const seededMainCropSettings: MainCropSettingsCMC = {
      crop1Id: mainCropsBeforeAndAfterRecommendation.before?.cropId,
      crop2Id: mainCropsBeforeAndAfterRecommendation.after?.cropId,
      seedingDate: mainCropsBeforeAndAfterRecommendation.after?.seedingStartDate ?? undefined,
      harvestDate: mainCropsBeforeAndAfterRecommendation.before?.harvestEndDate ?? undefined,
    };

    const selectedCropFamilies = [seededMainCropSettings.crop1Id, seededMainCropSettings.crop2Id]
      .filter((cropId) => cropId !== undefined)
      .map((cropId) => crops?.find((crop) => crop.id === cropId)?.cropFamily);

    setMainCropSettings(seededMainCropSettings);
    setCropRotationFamiliesToExclude(CROP_FAMILIES_TO_EXCLUDE_OPTIONS.filter((family) => selectedCropFamilies.includes(family)));
    setSoilType(settingsCMC?.[0]?.recommendation?.settings?.soilType || undefined);
    setSoilPh(settingsCMC?.[0]?.recommendation?.settings?.soilPh || undefined);
    setDrought(typeof settingsCMC?.[0]?.recommendation?.settings?.isDrought === "boolean" ? settingsCMC?.[0]?.recommendation?.settings?.isDrought : undefined);
    setIsNewCropMix(true);
    setCurrentStep(CMC_ROUTES.SETTINGS);
  }, [
    crops,
    practicesResponse,
    selectedRecommendationItemOnTimeline,
    setCropRotationFamiliesToExclude,
    setCurrentStep,
    setDrought,
    setIsNewCropMix,
    setMainCropSettings,
    setSoilPh,
    setSoilType,
    settingsCMC,
  ]);

  const hasSeededDraftFromFieldViewRef = useRef<boolean>(false);

  useEffect(() => {
    if (!visible || params.cropMixId) {
      hasSeededDraftFromFieldViewRef.current = false;
      return;
    }
    if (!fieldId || !practicesResponse || isLoadingPracticesResponse) return;
    if (hasSeededDraftFromFieldViewRef.current) return;

    hasSeededDraftFromFieldViewRef.current = true;
    seedDraftFromFieldView();
  }, [fieldId, isLoadingPracticesResponse, params.cropMixId, practicesResponse, seedDraftFromFieldView, visible]);

  useEffect(() => {
    if (!isVisible || !isCmcPath || params.cropMixId || !isPageEntry || !areSettingsSettled || isDraftMix) return;

    const mostRecentMix = getMostRecentlyUpdatedMix(settingsCMC);
    if (!mostRecentMix) return;

    navigate(`${basePath}cmc/${mostRecentMix.id}${location.search}`);
  }, [areSettingsSettled, basePath, isCmcPath, isDraftMix, isPageEntry, isVisible, location.search, navigate, params.cropMixId, settingsCMC]);

  useEffect(() => {
    if (isDraft && currentStep !== CMC_ROUTES.SETTINGS) setCurrentStep(CMC_ROUTES.SETTINGS);
  }, [currentStep, isDraft, setCurrentStep]);

  useEffect(() => {
    if (!isCmcPath) setIsDraftMix(false);

    return () => setIsDraftMix(false);
  }, [isCmcPath, setIsDraftMix]);

  useEffect(() => {
    if (visible) {
      setRegionId(regionId);
    }

    if (!visible && isCmcPath) {
      navigate(`${basePath}cmc${location.search}`);
    }
  }, [basePath, isCmcPath, location.search, navigate, regionId, setRegionId, visible]);

  useEffect(() => {
    if (isDraft) return;
    if (isPendingCropMixComposerResponse || isPendingSettingsCMC || isPendingCropsCMC) return;

    if (!params.cropMixId) {
      resetSettings();
      return;
    }

    if (params.cropMixId === activeCropMixId) return;

    // if the crop mix id is not the same as the active crop mix id, we need to reset the settings
    resetSettings();

    const activeCropMixSettings = settingsCMC?.find((mix) => mix.id === params.cropMixId);
    if (!activeCropMixSettings) {
      navigate(`${basePath}cmc${location.search}`);
      return;
    }

    updateLocalSettings(activeCropMixSettings, cropsCMC || [], params.cropMixId);
  }, [
    activeCropMixId,
    basePath,
    cropsCMC,
    isDraft,
    isPendingCropMixComposerResponse,
    isPendingCropsCMC,
    isPendingSettingsCMC,
    location.search,
    navigate,
    params.cropMixId,
    resetSettings,
    setCurrentStep,
    settingsCMC,
    updateLocalSettings,
  ]);

  useEffect(() => {
    if (!activeCropMixId || activeCropMixId !== params.cropMixId) return;

    if (params.step === undefined && currentStep) {
      navigate(`${basePath}cmc/${activeCropMixId}/${currentStep}${location.search}`);
      return;
    }

    if (isValidStepFromPath() && (params.step !== currentStep || currentStep === null)) {
      setCurrentStep(params.step as CMC_ROUTES);
      navigate(`${basePath}cmc/${activeCropMixId}/${params.step}${location.search}`);
      return;
    }

    if (!isValidStepFromPath()) {
      navigate(`${basePath}cmc/${activeCropMixId}/${location.search}`);
    }
  }, [activeCropMixId, basePath, currentStep, isValidStepFromPath, location.search, navigate, params.cropMixId, params.step, setCurrentStep, settingsCMC]);

  useEffect(() => {
    if (
      isLoadingPracticesResponse ||
      isPendingCropsCMC ||
      isPendingSettingsCMC ||
      isPendingScoreFactors ||
      ((currentStep === CMC_ROUTES.COMPOSITION || currentStep === CMC_ROUTES.OVERVIEW || currentStep === CMC_ROUTES.COM_MIXES) &&
        (!cropMixComposerResponse || cropMixComposerResponse?.length === 0))
    ) {
      setIsPageLoading(true);
    } else {
      setIsPageLoading(false);
    }
  }, [isLoadingPracticesResponse, isPendingCropsCMC, isPendingScoreFactors, isPendingSettingsCMC, cropMixComposerResponse, setIsPageLoading, currentStep]);

  const content = () => (
    <>
      {(isPageEntry && isPendingSettingsCMC) || (((fieldId && !params.cropMixId) || params.cropMixId !== activeCropMixId) && !isDraft) ? (
        <PageSpin />
      ) : (
        <Flex className="body" gap="4rem" justify="center">
          {currentStep === CMC_ROUTES.SETTINGS ? (
            <Settings
              setShowInfoDrawer={setShowInfoDrawer}
              t={t}
              fieldId={fieldId}
              selectedRecommendationItemOnTimeline={selectedRecommendationItemOnTimeline}
            />
          ) : currentStep === CMC_ROUTES.GOALS ? (
            <Goals setShowInfoDrawer={setShowInfoDrawer} maxSelectableItems={MAX_SELECTABLE_GOALS} t={t} />
          ) : currentStep === CMC_ROUTES.COMPOSITION ? (
            <MixComposition fieldId={fieldId} />
          ) : currentStep === CMC_ROUTES.OVERVIEW ? (
            <MixOverview fieldId={fieldId} isRegeneratingSmartMix={isRegeneratingSmartMix} onRegenerateSmartMix={handleRegenerateSmartMix} />
          ) : currentStep === CMC_ROUTES.COM_MIXES ? (
            <CommercialCropMixesContent isDrawer={openInDrawer} />
          ) : null}
        </Flex>
      )}
      <ExtraInfo setVisible={setShowInfoDrawer} type="why-side-crops-matter" visible={showInfoDrawer} />
    </>
  );

  const footer = (
    <CropMixComposerFooter
      step={currentStep}
      onApplyToFieldClicked={handleApplyToField}
      onNextClicked={handleNextButton}
      onBackClicked={handleBackButton}
      fieldId={fieldId}
    />
  );

  const navigationCrumbs = useMemo(
    () => ({
      crumbs: [{ name: t("pageTitle") }],
      additionalData: (activeCropMixId || isDraft) && (
        <CropMixComposerHeader
          basePath={basePath}
          activeCropMixId={activeCropMixId}
          isCommercialCropMix={currentStep === CMC_ROUTES.COM_MIXES}
          isPageLoading={isPageLoading}
          isDraft={isDraft}
        />
      ),
    }),
    [t, basePath, activeCropMixId, currentStep, isPageLoading, isDraft]
  );

  // We need to set the navigation crumbs here because otherwise the mix selector dropdown is not updated correctly in the header
  useEffect(() => {
    if (!openInDrawer) setNavigationCrumbs(navigationCrumbs);
  }, [navigationCrumbs, openInDrawer, setNavigationCrumbs, currentStep]);

  const shouldShowFooter = currentStep && currentStep !== CMC_ROUTES.COM_MIXES && (activeCropMixId || isDraft);

  const overlayClassName = currentStep !== CMC_ROUTES.COM_MIXES ? `crop-mix-composer-overlay step-${currentStep}` : "";
  if (!openInDrawer) {
    return (
      <MainPage fullWidth pageName="crop-mix-composer-page" share={{ title: t("pageTitle") }} showMobileAlert>
        <div className="crop-mix-composer-page">
          <Flex vertical className={overlayClassName}>
            <div className="crop-mix-composer-page-content">{content()}</div>
            {shouldShowFooter ? <div className="crop-mix-composer-page-footer">{footer}</div> : null}
          </Flex>
        </div>
      </MainPage>
    );
  }

  return (
    <Drawer
      className={overlayClassName}
      dataTestId="crop-mix-composer-overlay"
      visible={isVisible}
      placement="bottom"
      height="calc(100vh - 1rem)"
      fullWidth
      onClose={handleClose}
      push={false}
      hideCloseIcon={currentStep === CMC_ROUTES.COM_MIXES}
      title={
        <CropMixComposerHeader basePath={basePath} activeCropMixId={activeCropMixId} isDrawer isCommercialCropMix={currentStep === CMC_ROUTES.COM_MIXES} />
      }
      footer={shouldShowFooter ? footer : null}
    >
      {content()}
    </Drawer>
  );
};
